import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { POST_QUERY_KEY } from './useGetPost';

export type ApiRequest_Update_Post = {
  title?: string;
  content?: string;
  is_pinned?: boolean;
  metadata?: Record<string, unknown>;
};

export type ApiResponse_Update_Post = {
  id: number;
  title: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  community_id?: number;
  group_negotiation_id?: number;
  author: {
    id: number;
    user: {
      id: string;
      full_name: string;
      email: string;
    };
  };
  _count: {
    replies: number;
    likes: number;
  };
};

const METHOD = 'PUT';
const ENDPOINT = 'posts/{postId}';

export const useUpdatePost = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { postId: number } & ApiRequest_Update_Post) => {
      try {
        const { postId, ...updateData } = params;
        const data = await handleApiCall<ApiResponse_Update_Post, ApiRequest_Update_Post>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            postId: postId.toString(),
          }),
          updateData
        );

        enqueueSnackbar('Post updated successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to update post.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data) => {
      // Invalidate the specific post
      queryClient.invalidateQueries({
        queryKey: POST_QUERY_KEY(data.id),
      });
      
      // Invalidate community posts if it's a community post
      if (data.community_id) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/community/${data.community_id}`);
            }
            return false;
          },
        });
      }
      
      // Invalidate group posts if it's a group post
      if (data.group_negotiation_id) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/group/${data.group_negotiation_id}`);
            }
            return false;
          },
        });
      }
    },
  });
};